import { useCallback, useEffect, useState } from 'react';
import { menuApi, CATEGORY_COLORS } from '../../lib/menuApi';
import MenuErrorBanner from './MenuErrorBanner';
import ItemForm from './ItemForm';
import TemplatePicker from './TemplatePicker';
import MenuImport from './MenuImport';

const btn = 'px-2.5 py-1.5 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-purple-400 disabled:opacity-40';
const SECTIONS = [['edit', 'Edit menu'], ['templates', 'Templates'], ['import', 'Import']];

/** Move ids[i] by delta; returns the new list or null when it would leave the list. */
const moved = (ids, i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= ids.length) return null;
    const next = [...ids];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
};

/**
 * Menu editor for owners/admins/managers (spec D6/D7, T4): categories (add,
 * rename, color, reorder, deactivate, reactivate), items per category (add,
 * edit, delete, 86, reorder), template picker and import. Reads
 * GET /api/menu/manage and reloads after every write. The sample tenant and
 * expired sessions show MenuErrorBanner instead of the editor.
 */
export default function MenuEditor({ initialSection = 'edit' }) {
    const [section, setSection] = useState(initialSection);
    const [menu, setMenu] = useState(null);
    const [selected, setSelected] = useState('');
    const [loadError, setLoadError] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [editing, setEditing] = useState(null); // null | 'new' | item
    const [renaming, setRenaming] = useState(null); // {id, name}
    const [newCategory, setNewCategory] = useState('');
    const [notice, setNotice] = useState('');

    const load = useCallback(async () => {
        try {
            const data = await menuApi.manage();
            setMenu(data);
            setLoadError(null);
            setSelected((cur) => (data.categories.some((c) => c.id === cur) ? cur : data.categories[0]?.id || ''));
        } catch (err) {
            setLoadError(err);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    /** Run a write, then reload; errors stay on screen. */
    const write = async (fn, okNotice = '') => {
        setBusy(true);
        setError(null);
        setNotice('');
        try {
            const result = await fn();
            await load();
            setNotice(okNotice);
            return result ?? true;
        } catch (err) {
            setError(err);
            return null;
        } finally {
            setBusy(false);
        }
    };

    if (loadError && !menu) {
        return (
            <div className="space-y-3">
                <MenuErrorBanner error={loadError} />
                {loadError.kind !== 'sample' && loadError.kind !== 'auth' && loadError.kind !== 'forbidden' && (
                    <button type="button" onClick={load} className={`${btn} bg-gray-200`}>Try again</button>
                )}
            </div>
        );
    }
    if (!menu) return <p role="status" className="text-gray-500">Loading menu…</p>;

    const categories = menu.categories;
    const activeCategories = categories.filter((c) => c.isActive);
    const current = categories.find((c) => c.id === selected);
    const items = menu.items.filter((i) => i.category === selected);
    const catIds = categories.map((c) => c.id);
    const itemIds = items.map((i) => i.id);

    const addCategory = async (e) => {
        e.preventDefault();
        const name = newCategory.trim();
        if (!name) return;
        const res = await write(() => menuApi.createCategory({ name }), `Added ${name}.`);
        if (res) { setNewCategory(''); if (res.category) setSelected(res.category.id); }
    };

    const saveItem = async (body) => {
        if (editing === 'new') {
            const ok = await write(() => menuApi.createItem(body), `Added ${body.name}.`);
            if (ok) setEditing(null);
            return;
        }
        const patch = { ...body };
        if (patch.category === editing.category) delete patch.category; // unchanged: keep it even if inactive
        const ok = await write(() => menuApi.updateItem(editing.id, patch), `Saved ${body.name}.`);
        if (ok) setEditing(null);
    };

    const deleteItem = (item) => {
        if (!window.confirm(`Delete ${item.name}? Items already on past orders are hidden instead.`)) return;
        write(() => menuApi.deleteItem(item.id), `Deleted ${item.name}.`);
    };

    const deleteCategory = async (cat) => {
        const hasItems = menu.items.some((i) => i.category === cat.id);
        const prompt = hasItems
            ? `${cat.name} has items. Deactivate it? It disappears from the POS and can be reactivated later.`
            : `Delete the empty category ${cat.name}?`;
        if (!window.confirm(prompt)) return;
        write(() => menuApi.deleteCategory(cat.id, hasItems), hasItems ? `Deactivated ${cat.name}.` : `Deleted ${cat.name}.`);
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap gap-2" role="group" aria-label="Menu tools">
                {SECTIONS.map(([id, label]) => (
                    <button key={id} type="button" aria-pressed={section === id} onClick={() => setSection(id)}
                        className={`${btn} px-4 py-2 ${section === id ? 'bg-purple-600 text-white' : 'bg-white text-gray-700 border border-gray-300'}`}>
                        {label}
                    </button>
                ))}
            </div>

            <MenuErrorBanner error={error} />
            {notice && <p role="status" className="text-sm text-green-700">{notice}</p>}

            {section === 'templates' && (
                <TemplatePicker onApplied={async (r, t) => {
                    await load();
                    setNotice(`${t.name}: added ${r.created.length} categor${r.created.length === 1 ? 'y' : 'ies'}. Now add your items.`);
                    setSection('edit');
                }} />
            )}
            {section === 'import' && <MenuImport onImported={load} />}

            {section === 'edit' && (
                <div className="grid gap-4 md:grid-cols-[18rem_1fr]">
                    {/* Categories */}
                    <section aria-labelledby="menu-categories-title" className="bg-white rounded-xl shadow p-3 space-y-2">
                        <h3 id="menu-categories-title" className="font-bold text-gray-900">Categories</h3>
                        {categories.length === 0 && (
                            <p className="text-sm text-gray-600">No categories yet. Add one below or start from a template.</p>
                        )}
                        <ul className="space-y-1.5">
                            {categories.map((c, i) => (
                                <li key={c.id} className={`rounded-lg border p-2 ${selected === c.id ? 'border-purple-500 bg-purple-50' : 'border-gray-200'}`}>
                                    {renaming?.id === c.id ? (
                                        <form className="flex gap-1.5" onSubmit={async (e) => {
                                            e.preventDefault();
                                            if (await write(() => menuApi.updateCategory(c.id, { name: renaming.name }), 'Renamed.')) setRenaming(null);
                                        }}>
                                            <label htmlFor={`rename-${c.id}`} className="sr-only">New name for {c.name}</label>
                                            <input id={`rename-${c.id}`} autoFocus value={renaming.name} maxLength={50}
                                                onChange={(e) => setRenaming({ id: c.id, name: e.target.value })}
                                                onKeyDown={(e) => { if (e.key === 'Escape') setRenaming(null); }}
                                                className="flex-1 min-w-0 rounded border border-gray-300 px-2 py-1 text-gray-900" />
                                            <button type="submit" className={`${btn} bg-purple-600 text-white`}>Save</button>
                                        </form>
                                    ) : (
                                        <button type="button" onClick={() => { setSelected(c.id); setEditing(null); }}
                                            aria-pressed={selected === c.id}
                                            className="w-full flex items-center gap-2 text-left focus:outline-none focus:ring-2 focus:ring-purple-400 rounded">
                                            <span className={`inline-block w-3 h-3 rounded-full ${c.color}`} aria-hidden="true" />
                                            <span className={`flex-1 font-medium ${c.isActive ? 'text-gray-900' : 'text-gray-400 line-through'}`}>{c.name}</span>
                                            {!c.isActive && <span className="text-xs text-gray-500">inactive</span>}
                                        </button>
                                    )}
                                    {selected === c.id && renaming?.id !== c.id && (
                                        <div className="flex flex-wrap gap-1 mt-2">
                                            <button type="button" className={`${btn} bg-gray-100`} aria-label={`Move ${c.name} up`} disabled={busy || i === 0}
                                                onClick={() => write(() => menuApi.reorderCategories(moved(catIds, i, -1)))}>↑</button>
                                            <button type="button" className={`${btn} bg-gray-100`} aria-label={`Move ${c.name} down`} disabled={busy || i === categories.length - 1}
                                                onClick={() => write(() => menuApi.reorderCategories(moved(catIds, i, 1)))}>↓</button>
                                            <button type="button" className={`${btn} bg-gray-100`} onClick={() => setRenaming({ id: c.id, name: c.name })}>Rename</button>
                                            <label htmlFor={`color-${c.id}`} className="sr-only">Color for {c.name}</label>
                                            <select id={`color-${c.id}`} value={c.color} disabled={busy}
                                                onChange={(e) => write(() => menuApi.updateCategory(c.id, { color: e.target.value }))}
                                                className="rounded-lg border border-gray-300 text-sm px-1 py-1 text-gray-800">
                                                {CATEGORY_COLORS.map((col) => <option key={col.value} value={col.value}>{col.label}</option>)}
                                            </select>
                                            {c.isActive ? (
                                                <button type="button" className={`${btn} bg-red-50 text-red-700`} disabled={busy} onClick={() => deleteCategory(c)}>
                                                    {menu.items.some((it) => it.category === c.id) ? 'Deactivate' : 'Delete'}
                                                </button>
                                            ) : (
                                                <button type="button" className={`${btn} bg-green-50 text-green-700`} disabled={busy}
                                                    onClick={() => write(() => menuApi.updateCategory(c.id, { is_active: true }), `Reactivated ${c.name}.`)}>
                                                    Reactivate
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                        <form onSubmit={addCategory} className="flex gap-1.5 pt-2 border-t">
                            <label htmlFor="new-category" className="sr-only">New category name</label>
                            <input id="new-category" value={newCategory} maxLength={50} placeholder="New category, e.g. Kids Menu"
                                onChange={(e) => setNewCategory(e.target.value)}
                                className="flex-1 min-w-0 rounded-lg border border-gray-300 px-2 py-1.5 text-gray-900" />
                            <button type="submit" disabled={busy || !newCategory.trim()} className={`${btn} bg-purple-600 text-white`}>Add</button>
                        </form>
                    </section>

                    {/* Items */}
                    <section aria-labelledby="menu-items-title" className="space-y-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <h3 id="menu-items-title" className="font-bold text-gray-900 text-lg">
                                {current ? `${current.name} items` : 'Items'}
                            </h3>
                            {current && editing === null && (
                                <button type="button" onClick={() => setEditing('new')} disabled={!current.isActive}
                                    className={`${btn} px-4 py-2 bg-purple-600 text-white`}>
                                    + Add item
                                </button>
                            )}
                        </div>
                        {current && !current.isActive && (
                            <p className="text-sm text-gray-600">This category is inactive. Reactivate it to add items or show it on the POS.</p>
                        )}
                        {editing !== null && (
                            <ItemForm
                                key={editing === 'new' ? 'new' : editing.id}
                                item={editing === 'new' ? null : editing}
                                categories={editing === 'new' || activeCategories.some((c) => c.id === editing.category)
                                    ? activeCategories
                                    : [...activeCategories, categories.find((c) => c.id === editing.category)].filter(Boolean)}
                                defaultCategory={selected}
                                busy={busy}
                                onSubmit={saveItem}
                                onCancel={() => setEditing(null)}
                            />
                        )}
                        {!current && categories.length > 0 && <p className="text-gray-600">Pick a category.</p>}
                        {current && items.length === 0 && editing === null && (
                            <p className="text-gray-600">No items in {current.name} yet.</p>
                        )}
                        <ul className="space-y-2">
                            {items.map((item, i) => (
                                <li key={item.id} className="bg-white rounded-xl shadow p-3 flex flex-wrap items-center gap-2">
                                    <div className="flex-1 min-w-[10rem]">
                                        <p className={`font-semibold ${item.isAvailable ? 'text-gray-900' : 'text-gray-400'}`}>{item.name}</p>
                                        <p className="text-sm text-gray-600">
                                            ${Number(item.price).toFixed(2)}
                                            {item.is86d && <span className="ml-2 text-red-700 font-semibold">86'd</span>}
                                            {!item.isAvailable && <span className="ml-2 text-gray-500">hidden</span>}
                                        </p>
                                    </div>
                                    <button type="button" className={`${btn} bg-gray-100`} aria-label={`Move ${item.name} up`} disabled={busy || i === 0}
                                        onClick={() => write(() => menuApi.reorderItems(moved(itemIds, i, -1)))}>↑</button>
                                    <button type="button" className={`${btn} bg-gray-100`} aria-label={`Move ${item.name} down`} disabled={busy || i === items.length - 1}
                                        onClick={() => write(() => menuApi.reorderItems(moved(itemIds, i, 1)))}>↓</button>
                                    <button type="button" aria-pressed={!!item.is86d} disabled={busy}
                                        aria-label={item.is86d ? `Un-86 ${item.name}` : `86 ${item.name}`}
                                        className={`${btn} ${item.is86d ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-800'}`}
                                        onClick={() => write(() => menuApi.set86(item.id, !item.is86d))}>
                                        86
                                    </button>
                                    <button type="button" className={`${btn} bg-gray-100`} aria-label={`Edit ${item.name}`} onClick={() => setEditing(item)}>Edit</button>
                                    <button type="button" className={`${btn} bg-red-50 text-red-700`} aria-label={`Delete ${item.name}`} disabled={busy} onClick={() => deleteItem(item)}>Delete</button>
                                </li>
                            ))}
                        </ul>
                    </section>
                </div>
            )}
        </div>
    );
}
